import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Heading,
  Text,
  Card,
  CardBody,
  SimpleGrid,
  FormHelperText,
} from '@chakra-ui/react';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

export interface BookingConstraints {
  maxConcurrentBookings: number;
  minAdvanceNoticeHours: number;
  maxAdvanceBookingDays: number;
  sameDayCutoffHours: number;
  bufferTimeMinutes: number;
  defaultServiceDurationMinutes: number;
  maxServiceDurationMinutes: number;
}

export interface AppSettings {
  id: string;
  paymentStrategy?: string;
  upfrontPercentage?: number;
  businessHours?: any;
  bookingConstraints?: BookingConstraints;
  serviceAreaSettings?: any;
  bookingPoolSettings?: any;
  holidays?: any[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
}

const DEFAULT_CONSTRAINTS: BookingConstraints = {
  maxConcurrentBookings: 3,
  minAdvanceNoticeHours: 2,
  maxAdvanceBookingDays: 30,
  sameDayCutoffHours: 4,
  bufferTimeMinutes: 15,
  defaultServiceDurationMinutes: 60,
  maxServiceDurationMinutes: 180,
};

const BookingConstraintsSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [constraints, setConstraints] = useState<BookingConstraints>(DEFAULT_CONSTRAINTS);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const settings = settingsSnap.data() as AppSettings;
        if (settings.bookingConstraints) {
          setConstraints(settings.bookingConstraints);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load booking constraints settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof BookingConstraints, value: number) => {
    setConstraints(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          bookingConstraints: constraints,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          paymentStrategy: 'collect_full_at_booking',
          bookingConstraints: constraints,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Booking constraints updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save booking constraints settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConstraints(DEFAULT_CONSTRAINTS);
    toast({
      title: 'Reset',
      description: 'Booking constraints reset to defaults',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          mb={4}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
        >
          Booking Constraints
        </Heading>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Configure rules that limit when and how customers can book appointments.
              These constraints help manage booking availability and prevent overbooking.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Advance Notice Settings */}
            <VStack align="stretch" spacing={4}>
              <Heading size="sm">Advance Notice</Heading>
              
              <FormControl>
                <FormLabel>Minimum Advance Notice (Hours)</FormLabel>
                <NumberInput
                  value={constraints.minAdvanceNoticeHours}
                  onChange={(_, val) => handleChange('minAdvanceNoticeHours', val)}
                  min={0}
                  max={48}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>How many hours in advance can customers book?</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Same Day Cutoff (Hours)</FormLabel>
                <NumberInput
                  value={constraints.sameDayCutoffHours}
                  onChange={(_, val) => handleChange('sameDayCutoffHours', val)}
                  min={1}
                  max={12}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>How many hours before the appointment for same-day booking?</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Maximum Advance Booking (Days)</FormLabel>
                <NumberInput
                  value={constraints.maxAdvanceBookingDays}
                  onChange={(_, val) => handleChange('maxAdvanceBookingDays', val)}
                  min={7}
                  max={365}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>How far in advance can customers book?</FormHelperText>
              </FormControl>
            </VStack>

            {/* Service Duration & Scheduling */}
            <VStack align="stretch" spacing={4}>
              <Heading size="sm">Service Duration & Scheduling</Heading>
              
              <FormControl>
                <FormLabel>Default Service Duration (Minutes)</FormLabel>
                <NumberInput
                  value={constraints.defaultServiceDurationMinutes}
                  onChange={(_, val) => handleChange('defaultServiceDurationMinutes', val)}
                  min={15}
                  max={120}
                  step={15}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Default appointment length</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Maximum Service Duration (Minutes)</FormLabel>
                <NumberInput
                  value={constraints.maxServiceDurationMinutes}
                  onChange={(_, val) => handleChange('maxServiceDurationMinutes', val)}
                  min={60}
                  max={480}
                  step={15}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Maximum allowed appointment length</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Buffer Time Between Bookings (Minutes)</FormLabel>
                <NumberInput
                  value={constraints.bufferTimeMinutes}
                  onChange={(_, val) => handleChange('bufferTimeMinutes', val)}
                  min={0}
                  max={60}
                  step={5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Time buffer between consecutive appointments</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Max Concurrent Bookings</FormLabel>
                <NumberInput
                  value={constraints.maxConcurrentBookings}
                  onChange={(_, val) => handleChange('maxConcurrentBookings', val)}
                  min={1}
                  max={10}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Maximum number of bookings per time slot</FormHelperText>
              </FormControl>
            </VStack>
          </SimpleGrid>
        </CardBody>
      </Card>

      <HStack spacing={3} mt={6}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Constraints
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
        >
          Reset to Defaults
        </Button>
      </HStack>
    </Box>
  );
};

export default BookingConstraintsSettings;

